<template>
  <dashboard-layout title="System Logs">
    <template #actions>
      <button class="style-toggle" :class="{ active: styleOnly }" @click="toggleStyleOnly" title="Show only updateStyleProfile logs">
        <nav-icon name="brush" :size="14" /> Style Updates
      </button>
      <search-box v-model="search" placeholder="Search logs…" />
      <button class="btn-export" @click="downloadCSV">
        <nav-icon name="download" :size="15" /> Export CSV
      </button>
    </template>

    <stat-grid>
      <stat-card label="Loaded logs" :value="loadedCount.toLocaleString('en-US')" icon="server" accent />
      <stat-card label="Errors" :value="errorCount.toLocaleString('en-US')" icon="zap" />
      <stat-card label="Warnings" :value="warnCount.toLocaleString('en-US')" icon="activity" />
      <stat-card label="Functions" :value="distinctFunctions.toLocaleString('en-US')" icon="terminal" />
    </stat-grid>

    <data-panel flush>
      <template #title>System Logs <span class="muted">({{ filtered.length.toLocaleString('en-US') }})</span></template>
      <template #actions>
        <div class="level-chips">
          <button
            v-for="level in availableLevels"
            :key="level"
            class="lvl-chip"
            :class="[`lvl-${level}`, { active: selectedLevels.includes(level) }]"
            @click="toggleLevel(level)"
          >{{ level }}</button>
        </div>
      </template>

      <loading-state v-if="loading && list.length === 0" label="Loading logs…" />
      <template v-else>
        <data-table
          :columns="columns"
          :rows="filtered"
          row-key="id"
          clickable
          @row-click="(row, event) => openLogDetail(row.id, event)"
        >
          <template #cell-createdAt="{ row }">
            <span class="nowrap">{{ formatDate(row.createdAt) }}</span>
          </template>
          <template #cell-level="{ row }">
            <status-badge :variant="levelVariant(row.level)" :label="row.level || '—'" />
          </template>
          <template #cell-tools="{ row }">
            <template v-if="parseMeta(row.metadata)?.toolsUsed?.length">
              <span class="tool-chip" v-for="t in parseMeta(row.metadata).toolsUsed" :key="t">{{ t }}</span>
            </template>
            <template v-else-if="parseMeta(row.metadata)?.toolCallDetails?.length">
              <span class="tool-chip" v-for="t in parseMeta(row.metadata).toolCallDetails" :key="t.name">{{ t.name }} <span class="tool-ms" v-if="t.durationMs">{{ t.durationMs }}ms</span></span>
            </template>
            <span v-else class="dim">-</span>
          </template>
          <template #cell-duration="{ row }">
            <span v-if="parseMeta(row.metadata)?.totalDurationMs" class="duration-chip">{{ parseMeta(row.metadata).totalDurationMs }}ms</span>
            <span v-else-if="parseMeta(row.metadata)?.durationSeconds" class="duration-chip">{{ parseMeta(row.metadata).durationSeconds }}</span>
            <span v-else-if="parseMeta(row.metadata)?.duration" class="duration-chip">{{ parseMeta(row.metadata).duration > 1000 ? (parseMeta(row.metadata).duration / 1000).toFixed(1) + 's' : parseMeta(row.metadata).duration + 'ms' }}</span>
            <span v-else class="dim">-</span>
          </template>
          <template #cell-error="{ row }">
            <pre v-if="row.error" class="metadata-pre">{{ row.error }}</pre>
            <span v-else class="dim">-</span>
          </template>
          <template #cell-userId="{ row }">
            <span v-if="row.userId" class="user-id-link" @click.stop="openUserDetail(row.userId, $event)">{{ row.userId }}</span>
            <span v-else class="dim">-</span>
          </template>
          <template #cell-metadata="{ row }">
            <pre class="metadata-pre">{{ row.metadata }}</pre>
          </template>
          <template #empty>No system logs found</template>
        </data-table>

        <div v-if="loadingMore" class="loading-more">
          <div class="spinner"></div>
          <span>Loading more logs...</span>
        </div>
        <div v-else-if="hasMore && !loading" class="load-more-row">
          <button @click="loadMore" class="load-more-btn">
            Load more ({{ list.length }} loaded{{ filtered.length !== list.length ? `, ${filtered.length} after filter` : '' }})
          </button>
          <span class="load-more-hint" v-if="filtered.length < 5 && search">Search may need more pages — keep loading.</span>
        </div>
        <div v-if="!hasMore && filtered.length > 0" class="end-message">
          No more logs to load
        </div>
      </template>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseSystemLogService } from '../services/firebaseSystemLogService'
import NavIcon from './NavIcon.vue'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const columns = [
  { key: 'createdAt', label: 'Timestamp', width: '150px' },
  { key: 'level', label: 'Level', width: '90px' },
  { key: 'functionName', label: 'Function Name', width: '160px' },
  { key: 'message', label: 'Message' },
  { key: 'tools', label: 'Tools', width: '170px' },
  { key: 'duration', label: 'Duration', width: '90px' },
  { key: 'error', label: 'Error', width: '220px' },
  { key: 'userId', label: 'User ID', width: '150px' },
  { key: 'metadata', label: 'Metadata', width: '280px' }
]

export default {
  name: 'SystemLogsDashboard',
  components: {
    NavIcon, DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, LoadingState, StatusBadge
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseSystemLogService()
    const list = ref([])
    const filtered = ref([])
    const search = ref('')
    const loading = ref(true)
    const loadingMore = ref(false)
    const hasMore = ref(true)
    const lastDoc = ref(null)

    const availableLevels = ['error', 'info', 'success', 'warn']
    const selectedLevels = ref([...availableLevels])
    const styleOnly = ref(false)

    const load = async () => {
      loading.value = true
      try {
        const result = await service.getSystemLogsPaginated(40)
        list.value = result.logs
        lastDoc.value = result.lastVisible
        hasMore.value = result.hasMore
        applyFilter()
      } finally {
        loading.value = false
      }
    }

    const loadMore = async () => {
      if (loadingMore.value || !hasMore.value) return

      loadingMore.value = true
      try {
        const result = await service.getSystemLogsPaginated(40, lastDoc.value)
        list.value = [...list.value, ...result.logs]
        lastDoc.value = result.lastVisible
        hasMore.value = result.hasMore
        applyFilter()
      } finally {
        loadingMore.value = false
      }
    }

    const handleScroll = (e) => {
      const element = e.target
      const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight

      if (scrollPercentage > 0.8 && !loadingMore.value && hasMore.value) {
        loadMore()
      }
    }

    const applyFilter = () => {
      const q = search.value.toLowerCase()
      filtered.value = list.value.filter(item => {
        const levelMatch = selectedLevels.value.includes(item.level)
        if (!levelMatch) return false

        if (styleOnly.value && item.functionName !== 'updateStyleProfile') return false

        if (!q) return true

        return (
          (item.message && item.message.toLowerCase().includes(q)) ||
          (item.functionName && item.functionName.toLowerCase().includes(q)) ||
          (item.level && item.level.toLowerCase().includes(q)) ||
          (item.userId && item.userId.toLowerCase().includes(q)) ||
          (typeof item.metadata === 'string' && item.metadata.toLowerCase().includes(q))
        )
      })
    }

    const handleFilterChange = () => {
      applyFilter()
    }

    const toggleLevel = (level) => {
      const i = selectedLevels.value.indexOf(level)
      if (i === -1) selectedLevels.value.push(level)
      else selectedLevels.value.splice(i, 1)
      applyFilter()
    }

    const toggleStyleOnly = () => {
      styleOnly.value = !styleOnly.value
      applyFilter()
    }

    const metaCache = new Map()
    const parseMeta = (metadata) => {
      if (!metadata) return null
      if (metaCache.has(metadata)) return metaCache.get(metadata)
      try {
        const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : metadata
        metaCache.set(metadata, parsed)
        return parsed
      } catch {
        metaCache.set(metadata, null)
        return null
      }
    }

    const formatDate = (val) => {
      if (!val) return '-'
      const date = val.seconds ? new Date(val.seconds * 1000) : new Date(val)
      return date.toLocaleString('tr-TR')
    }

    const levelVariant = (level) => ({ error: 'danger', warn: 'warning', info: 'info', success: 'success' }[level] || 'neutral')

    const loadedCount = computed(() => list.value.length)
    const errorCount = computed(() => list.value.filter(i => i.level === 'error').length)
    const warnCount = computed(() => list.value.filter(i => i.level === 'warn').length)
    const distinctFunctions = computed(() => new Set(list.value.map(i => i.functionName).filter(Boolean)).size)

    const downloadCSV = () => {
      const dataToExport = filtered.value.length > 0 ? filtered.value : list.value
      if (dataToExport.length === 0) {
        alert('No data to export.')
        return
      }

      const headers = ['createdAt', 'functionName', 'level', 'lineNumber', 'message', 'error', 'metadata']

      const escapeCSV = (val) => {
        if (val === null || val === undefined) return ''
        let str = String(val)
        if (typeof val === 'object') str = JSON.stringify(val).replace(/"/g, '""')
        str = str.replace(/\r?\n/g, ' ')
        if (str.includes(',')) {
          str = `"${str}"`
        }
        return str
      }

      const rows = dataToExport.map(s => {
        return [
          formatDate(s.createdAt),
          s.functionName,
          s.level,
          s.lineNumber,
          s.message,
          escapeCSV(s.error),
          escapeCSV(s.metadata)
        ].join(',')
      })

      const csvContent = [headers.join(','), ...rows].join('\n')
      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', 'system_logs.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const openLogDetail = (logId, event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(`/system-log/${logId}`, '_blank')
      } else {
        router.push(`/system-log/${logId}`)
      }
    }

    const openUserDetail = (userId, event) => {
      if (!userId) return
      if (event.metaKey || event.ctrlKey) {
        window.open(`/user/${userId}`, '_blank')
      } else {
        router.push(`/user/${userId}`)
      }
    }

    watch(search, handleFilterChange)

    let scrollEl = null
    onMounted(() => {
      load()
      scrollEl = document.querySelector('.main-content')
      if (scrollEl) scrollEl.addEventListener('scroll', handleScroll)
    })
    onUnmounted(() => {
      if (scrollEl) scrollEl.removeEventListener('scroll', handleScroll)
    })

    return {
      list,
      filtered,
      search,
      loading,
      loadingMore,
      hasMore,
      columns,
      availableLevels,
      selectedLevels,
      styleOnly,
      toggleStyleOnly,
      toggleLevel,
      parseMeta,
      formatDate,
      levelVariant,
      loadedCount,
      errorCount,
      warnCount,
      distinctFunctions,
      downloadCSV,
      openLogDetail,
      openUserDetail,
      loadMore
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.nowrap { white-space: nowrap; }

.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  background: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: var(--radius);
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(var(--primary-rgb), 0.2);
}
.btn-export:hover { opacity: 0.9; box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.3); }

.style-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}
.style-toggle:hover { color: var(--text-primary); }
.style-toggle.active {
  background: rgba(var(--primary-rgb), 0.16);
  border-color: rgba(var(--primary-rgb), 0.5);
  color: var(--gradient-2);
}

.level-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.lvl-chip {
  padding: 5px 12px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  color: var(--text-secondary);
  font-size: 12.5px;
  text-transform: capitalize;
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}
.lvl-chip:hover { color: var(--text-primary); }
.lvl-chip.lvl-error.active { background: rgba(255, 107, 107, 0.16); border-color: rgba(255, 107, 107, 0.5); color: var(--danger); }
.lvl-chip.lvl-warn.active { background: rgba(248, 191, 113, 0.16); border-color: rgba(248, 191, 113, 0.5); color: var(--warning); }
.lvl-chip.lvl-info.active { background: rgba(34, 211, 238, 0.16); border-color: rgba(34, 211, 238, 0.5); color: var(--info); }
.lvl-chip.lvl-success.active { background: rgba(74, 222, 128, 0.16); border-color: rgba(74, 222, 128, 0.5); color: var(--success); }

.metadata-pre { background-color: #0f0f23; padding: 6px; border-radius: 4px; white-space: pre-wrap; word-break: break-all; font-size: 10px; max-height: 80px; overflow-y: auto; min-width: 150px; max-width: 300px; margin: 0; }
.tool-chip { display: inline-block; font-size: 10px; padding: 2px 6px; border-radius: 4px; background: rgba(34, 211, 174, 0.1); color: #22d3ae; margin: 1px 2px; font-family: monospace; white-space: nowrap; }
.tool-ms { font-size: 9px; opacity: 0.6; margin-left: 2px; }
.duration-chip { font-size: 11px; font-weight: 600; color: #fbbf24; font-family: monospace; }
.dim { color: rgba(255, 255, 255, 0.2); }
.user-id-link { color: var(--primary-color); cursor: pointer; text-decoration: underline; }
.user-id-link:hover { color: var(--gradient-2); }

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  color: var(--text-secondary);
  font-size: 13px;
}
.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(var(--primary-rgb), 0.3);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.end-message {
  text-align: center;
  padding: 16px;
  color: var(--primary-color);
  font-size: 12px;
  font-weight: 500;
  border-top: 1px solid var(--border-soft);
}
.load-more-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px;
  border-top: 1px solid var(--border-soft);
}
.load-more-btn {
  padding: 8px 18px;
  background: rgba(var(--primary-rgb), 0.12);
  border: 1px solid rgba(var(--primary-rgb), 0.35);
  border-radius: 8px;
  color: var(--gradient-2);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.load-more-btn:hover {
  background: rgba(var(--primary-rgb), 0.22);
  border-color: rgba(var(--primary-rgb), 0.55);
}
.load-more-hint {
  font-size: 11px;
  color: #fbbf24;
  opacity: 0.85;
}
</style>
